import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import GameImage from '../components/GameImage';

const SearchPage = ({ gameData }) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const query = searchParams.get('q') || '';
  
  const [searchResults, setSearchResults] = useState([]);
  
  useEffect(() => {
    if (gameData && query) {
      const results = [];
      
      // Search through all categories and games
      Object.keys(gameData).forEach(category => {
        if (gameData[category] && Array.isArray(gameData[category])) {
          const matchingGames = gameData[category].filter(game => {
            // Search in name, description, and tags
            const nameMatch = game.name && game.name.toLowerCase().includes(query.toLowerCase());
            const descMatch = game.desc && game.desc.toLowerCase().includes(query.toLowerCase());
            const categoryMatch = game.tags && game.tags.toLowerCase().includes(query.toLowerCase());
            
            return nameMatch || descMatch || categoryMatch;
          });
          
          // Add category info to each matching game
          matchingGames.forEach(game => {
            results.push({
              ...game,
              category
            });
          });
        }
      });
      
      setSearchResults(results);
      console.log('Search results:', results);
    }
  }, [gameData, query]);

  return (
    <SearchContainer>
      <SearchHeader>
        <SearchTitle>Search Results for: "{query}"</SearchTitle>
        <ResultCount>{searchResults.length} games found</ResultCount>
      </SearchHeader>
      
      {searchResults.length > 0 ? (
        <GameGrid>
          {searchResults.map(game => (
            <GameCard key={`${game.category}-${game.id}`}>
              <Link to={`/game/${game.category}/${game.id}`}>
                <GameImage 
                  src={game.thumb300x300} 
                  fallbackSrc={game.thumb512x384} 
                  alt={game.name} 
                  style={{ width: '100%', height: 'auto', display: 'block' }}
                />
                <GameInfo>
                  <GameName>{game.name}</GameName>
                  <GameCategory>{game.category}</GameCategory>
                  <GameTags>{game.tags || ''}</GameTags>
                </GameInfo>
              </Link>
            </GameCard>
          ))}
        </GameGrid>
      ) : (
        <NoResults>
          <NoResultsText>No games found matching "{query}"</NoResultsText>
          <SuggestionText>Try searching for a different term or browse our categories</SuggestionText>
          <BrowseLink to="/">Browse All Games</BrowseLink>
        </NoResults>
      )}
    </SearchContainer>
  );
};

const SearchContainer = styled.div`
  padding: 10px 0;
`;

const SearchHeader = styled.div`
  margin-bottom: 20px;
`;

const SearchTitle = styled.h1`
  font-size: 24px;
  margin-bottom: 5px;
`;

const ResultCount = styled.div`
  color: #666;
  font-size: 14px;
`;

const GameGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 15px;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 480px) {
    grid-template-columns: repeat(1, 1fr);
  }
`;

const GameCard = styled.div`
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s;
  
  &:hover {
    transform: translateY(-5px);
  }
  
  a {
    text-decoration: none;
    color: inherit;
  }
`;

// GameThumbnail replaced by GameImage component

const GameInfo = styled.div`
  padding: 12px;
`;

const GameName = styled.h3`
  font-size: 16px;
  margin: 0 0 5px 0;
`;

const GameCategory = styled.div`
  font-size: 12px;
  color: #45b7d8;
  margin-bottom: 5px;
  text-transform: capitalize;
`;

const GameTags = styled.div`
  font-size: 12px;
  color: #666;
`;

const NoResults = styled.div`
  text-align: center;
  padding: 40px 0;
`;

const NoResultsText = styled.h2`
  font-size: 20px;
  margin-bottom: 10px;
`;

const SuggestionText = styled.p`
  color: #666;
  margin-bottom: 20px;
`;

const BrowseLink = styled(Link)`
  display: inline-block;
  background: #45b7d8;
  color: white;
  padding: 10px 20px;
  border-radius: 4px;
  text-decoration: none;
  
  &:hover {
    background: #3ca9c9;
  }
`;

export default SearchPage;
